// How many options a modifier group lets you pick.
// docs/operational-ux-2026-09/06-menu-architecture.md §4
//
// ─── WHY THIS IS ONE FUNCTION AND NOT SEVEN EXPRESSIONS ──────────────────────
//
// The same line was written out by hand in seven places — the guest menu twice,
// the POS picker twice, the offline menu, and both server catalogue builders:
//
//     const max = group.unlimited ? Infinity : Number(group.max_select ?? 1);
//
// Seven copies of a rule is six chances to disagree, and they already did: the
// offline menu read `group.max_select ?? (group.required ? 1 : undefined)`,
// which is a different rule wearing the same clothes.
//
// ─── THE `0` MEANING ─────────────────────────────────────────────────────────
//
// `menu_item_option_groups` has no `unlimited` column — only the business-level
// `modifier_groups` table got one (migration 20261013000000). An item-level
// group therefore had no way to say "pick as many as you like", and defaulted to
// `max_select: 1`: pick one protein, one extra, one anything. That is wrong for
// the overwhelmingly common Ghanaian SMB modifier, which is a flat band of free
// proteins and paid extras with no enforcement at all (field study, screenshot
// 1). A merchant adding "Extras" had to find and clear a numeric cap to get the
// behaviour they wanted.
//
// So `max_select <= 0` means unlimited, and the item editor now writes 0 for new
// groups. This is a widening, not a reinterpretation: a group capped at zero
// could not be selected from at all, which is not a state any merchant chose on
// purpose — every row where it appears is a row that was already broken.
//
// No migration is involved. `unlimited` keeps working exactly as it did for the
// reusable groups that carry it.

/** The shape every caller has in common. Deliberately loose — rows arrive from
 *  six different queries, some snake_case from PostgREST, some already mapped. */
export interface ModifierLimitSource {
  min_select?: number | null;
  max_select?: number | null;
  required?: boolean | null;
  /** Business-level reusable groups only; absent on item-level groups. */
  unlimited?: boolean | null;
}

/**
 * The maximum number of options selectable in this group. `Infinity` when the
 * group is unlimited — callers compare against it, they do not render it.
 */
export function resolveMaxSelect(group: ModifierLimitSource): number {
  if (group.unlimited) return Infinity;
  const raw = Number(group.max_select ?? 1);
  if (!Number.isFinite(raw) || raw <= 0) return Infinity;
  return raw;
}

/**
 * The minimum number of options that must be selected. `required` with no
 * explicit minimum means one.
 */
export function resolveMinSelect(group: ModifierLimitSource): number {
  const raw = Number(group.min_select ?? (group.required ? 1 : 0));
  if (!Number.isFinite(raw) || raw < 0) return 0;
  return raw;
}

/** True when the group behaves as a radio (pick exactly one of). */
export function isSingleSelect(group: ModifierLimitSource): boolean {
  return resolveMaxSelect(group) <= 1;
}

/**
 * The merchant-facing summary of a group's rules — "Optional · any number",
 * "Required · pick 1", "Pick 2–3". Used by the editor so the collapsed Rules
 * row states what it is hiding.
 */
export function describeModifierLimits(group: ModifierLimitSource): string {
  const min = resolveMinSelect(group);
  const max = resolveMaxSelect(group);
  const required = Boolean(group.required) || min > 0;

  const count =
    max === Infinity
      ? min > 0
        ? `at least ${min}`
        : 'any number'
      : min === max
        ? `pick ${max}`
        : min > 0
          ? `pick ${min}–${max}`
          : `up to ${max}`;

  return `${required ? 'Required' : 'Optional'} · ${count}`;
}
